import { useSelector } from "react-redux";
import { useQueryClient } from "react-query";
import { Spinner } from "react-bootstrap";
import FeatureLocked from "../pages/SharedComponents/FeatureLocked";
import Layout from "../pages/Layout/Layout";

/**
 * Route-level feature gate. Renders the page only when the company's active
 * package has the given feature flag set to strict-true; otherwise renders the
 * FeatureLocked upgrade panel.
 *
 * Why a wrapper (vs a per-page check):
 *  - It reads `companyDetails.package.features[feature]` — the SAME source the
 *    Apps-grid UPGRADE badges use — so what BLOCKS is always consistent with
 *    what shows a badge. (Per-page checks keyed off a data query's 402 were
 *    unreliable: some pages swallow the error in their DataTable and render
 *    anyway, so they opened despite the badge.)
 *  - The page component only MOUNTS when allowed, so a locked page never runs
 *    its hooks/queries — no hook-order crash, no stray gated API calls.
 *
 * companyDetails is populated by CompanyDetailsRouter before these routes
 * render; while it's briefly absent we show a spinner rather than guess.
 */
export default function FeatureGate({ feature, children }) {
    const redux = useSelector((state) => state.global.companyDetails);
    // The router already holds the loaded /company/details in the react-query cache; redux is
    // filled one effect later (and starts as {}), so reading the cache first means a locked
    // card never flashes - and never mounts its layout/requests - for a package that HAS the
    // feature. Same data, same strict === true rule.
    const cached = useQueryClient().getQueryData('getUserSubscription');
    // Restaurant accounts keep the original behaviour exactly (redux source, bare legacy panel).
    const isRestaurant = (cached ?? redux)?.company?.account_type === 'restaurant';
    const companyDetails = isRestaurant ? redux : (cached ?? redux);

    // Spin ONLY while the company payload is still loading. Once it has loaded,
    // an absent package/features (no active plan, or a retired tier whose package
    // no longer resolves) must fall through to FeatureLocked — never an infinite
    // spinner. Previously this keyed off `features` being truthy, so a company
    // with no resolvable package spun forever on every gated page.
    if (!companyDetails) {
        return (
            <div className="flex w-full h-[60vh] items-center justify-center">
                <Spinner as="span" variant="primary" animation="border" role="status" aria-hidden="true" />
            </div>
        );
    }

    const features = companyDetails?.package?.features;
    return features?.[feature] === true
        ? children
        : isRestaurant
            ? <FeatureLocked feature={feature} />
            : <Layout padding={false}><FeatureLocked feature={feature} /></Layout>;
}
